import React, { useState } from 'react';
import { FileEditIcon, XIcon } from 'lucide-react';
import { InvoiceDocument } from '../types/invoice';
import { formatAmount } from '../utils/currencyFormat';
interface DraftChangesPopoverProps {
  originalDocument: InvoiceDocument;
  draftDocument: InvoiceDocument;
  onClearDraft: () => void;
}
interface Change {
  field: string;
  original: string | number;
  draft: string | number;
  lineItemIndex?: number;
}
function getChanges(original: InvoiceDocument, draft: InvoiceDocument): Change[] {
  const changes: Change[] = [];
  // Check header-level changes
  if (original.subtotal !== draft.subtotal) {
    changes.push({
      field: 'Subtotal',
      original: formatAmount(original.subtotal),
      draft: formatAmount(draft.subtotal)
    });
  }
  if (original.taxAmt !== draft.taxAmt) {
    changes.push({
      field: 'Tax Amount',
      original: formatAmount(original.taxAmt),
      draft: formatAmount(draft.taxAmt)
    });
  }
  if (original.total !== draft.total) {
    changes.push({
      field: 'Total',
      original: formatAmount(original.total),
      draft: formatAmount(draft.total)
    });
  }
  // Check line item changes
  draft.lineItems.forEach((draftItem, index) => {
    const originalItem = original.lineItems[index];
    if (!originalItem) return;
    if (originalItem.taxCode !== draftItem.taxCode) {
      changes.push({
        field: 'Tax Code',
        original: originalItem.taxCode || 'None',
        draft: draftItem.taxCode || 'None',
        lineItemIndex: index
      });
    }
    if (originalItem.glCode !== draftItem.glCode) {
      changes.push({
        field: 'GL Code',
        original: originalItem.glCode || 'None',
        draft: draftItem.glCode || 'None',
        lineItemIndex: index
      });
    }
    if (originalItem.subtotal !== draftItem.subtotal) {
      changes.push({
        field: 'Line Subtotal',
        original: formatAmount(originalItem.subtotal),
        draft: formatAmount(draftItem.subtotal),
        lineItemIndex: index
      });
    }
    if (originalItem.taxAmt !== draftItem.taxAmt) {
      changes.push({
        field: 'Line Tax Amount',
        original: formatAmount(originalItem.taxAmt),
        draft: formatAmount(draftItem.taxAmt),
        lineItemIndex: index
      });
    }
    if (originalItem.unitPrice !== draftItem.unitPrice) {
      changes.push({
        field: 'Unit Price',
        original: formatAmount(originalItem.unitPrice),
        draft: formatAmount(draftItem.unitPrice),
        lineItemIndex: index
      });
    }
  });
  return changes;
}
export function DraftChangesPopover({
  originalDocument,
  draftDocument,
  onClearDraft
}: DraftChangesPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const changes = getChanges(originalDocument, draftDocument);
  return <div className="relative" data-id="element-831">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 rounded-full hover:bg-amber-200 transition-colors" data-id="element-832">
        <FileEditIcon size={14} className="text-amber-700" data-id="element-833" />
        <span className="text-xs font-medium text-amber-700" data-id="element-834">
          Draft Changes ({changes.length})
        </span>
      </button>

      {isOpen && <div className="absolute top-full left-0 mt-2 w-[480px] bg-white border border-amber-200 rounded-lg shadow-xl z-50" data-id="element-835">
          {/* Header */}
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between" data-id="element-836">
            <div className="flex items-center gap-2" data-id="element-837">
              <FileEditIcon className="w-4 h-4 text-amber-700" data-id="element-838" />
              <h3 className="text-sm font-semibold text-amber-900" data-id="element-839">
                Unsaved Changes ({changes.length})
              </h3>
            </div>
            <button onClick={e => {
          e.stopPropagation();
          setIsOpen(false);
        }} className="p-1 hover:bg-amber-100 rounded transition-colors" data-id="element-840">
              <XIcon className="w-4 h-4 text-amber-700" data-id="element-841" />
            </button>
          </div>

          {/* Changes List */}
          <div className="max-h-[400px] overflow-y-auto" data-id="element-842">
            {changes.length === 0 ? <div className="px-4 py-8 text-center text-sm text-gray-500" data-id="element-843">
                No changes detected
              </div> : <div className="divide-y divide-gray-100" data-id="element-844">
                {changes.map((change, index) => <div key={index} className="px-4 py-3 hover:bg-gray-50 transition-colors" data-id="element-845">
                    <div className="flex items-start justify-between gap-3" data-id="element-846">
                      <div className="flex-1 min-w-0" data-id="element-847">
                        <div className="text-xs font-semibold text-gray-900 mb-2" data-id="element-848">
                          {change.lineItemIndex !== undefined && <span className="text-blue-600" data-id="element-849">
                              Line {change.lineItemIndex + 1}:{' '}
                            </span>}
                          {change.field}
                        </div>
                        <div className="space-y-1" data-id="element-850">
                          <div className="flex items-center gap-2" data-id="element-851">
                            <span className="text-xs text-gray-500 font-medium min-w-[60px]" data-id="element-852">
                              Original:
                            </span>
                            <span className="text-xs text-red-700 bg-red-50 px-2 py-0.5 rounded font-mono" data-id="element-853">
                              {change.original}
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-854">
                            <span className="text-xs text-gray-500 font-medium min-w-[60px]" data-id="element-855">
                              Draft:
                            </span>
                            <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded font-mono" data-id="element-856">
                              {change.draft}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between" data-id="element-857">
            <p className="text-xs text-gray-600" data-id="element-858">
              Changes will be saved when you submit
            </p>
            <button onClick={e => {
          e.stopPropagation();
          onClearDraft();
          setIsOpen(false);
        }} className="text-xs text-red-600 hover:text-red-800 hover:underline font-medium" data-id="element-859">
              Discard All Changes
            </button>
          </div>
        </div>}
    </div>;
}